import React, { createContext, useContext, useEffect, useReducer } from "react";
import { cartReducer, initialState, State } from "./cart.reducer";
import { useCartQuery, useAddToCartMutation, useRemoveFromCartMutation, useUpdateCartQuantityMutation, useGuestCartListQuery } from "@framework/cart/all-cart";
import { useUI } from "@contexts/ui.context";
import { getCurrentLocationAndLanguage } from "@utils/location";
import { useConsolidate } from "@/hooks/consolidate";
export type CartItem = {
  id: number;
  name: string;
  productCode: string;
  quantity: number;
  cost: string;
  img: string;
};
interface CartProviderState extends State {
  addItemToCart: (item: CartItem, quantity: number) => Promise<void>;
  removeItemFromCart: (productCode: string) => Promise<void>;
  updateItemQuantity: (productCode: string, quantity: number) => Promise<void>;
  isInCart: (productCode: string) => boolean;
  getItemFromCart: (productCode: string) => CartItem | undefined;
  resetCart: () => void;
  refetch: () => void;
}
const CartContext = createContext<CartProviderState | undefined>(undefined);
CartContext.displayName = "CartContext";
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error(`useCart must be used within a CartProvider`);
  }
  return context;
};
export const CartProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    isAuthorized
  } = useUI();
  const {
    location
  } = getCurrentLocationAndLanguage();
  const {
    consolidated,
    subCart
  } = useConsolidate() as unknown as {
    consolidated: boolean;
    subCart?: {
      id: number;
    };
  };
  const cartQuery = !isAuthorized && location === 'kg' ? useGuestCartListQuery() : useCartQuery();
  const {
    data: cartData,
    isLoading,
    refetch
  } = cartQuery;
  const addToCartMutation = useAddToCartMutation();
  const removeFromCartMutation = useRemoveFromCartMutation();
  const updateCartQuantityMutation = useUpdateCartQuantityMutation();
  const [state, dispatch] = useReducer(cartReducer, initialState);
  useEffect(() => {
    if (isAuthorized || getCurrentLocationAndLanguage().location === 'kg') {
      refetch();
    }
  }, [isAuthorized, refetch]);
  useEffect(() => {
    if (!isLoading && cartData) {
      if (!isAuthorized && 'carts' in cartData && Array.isArray((cartData as any).carts) && (cartData as any).carts.length > 0) {
        const guestCart = (cartData as any).carts[0];
        const transformedItems = guestCart.items.map((item: any) => ({
          id: Number(item.id),
          name: item.name,
          productCode: item.productCode,
          quantity: item.quantity,
          cost: item.strCost || "",
          img: item.image || ""
        }));
        dispatch({
          type: "SET_CART",
          payload: {
            items: transformedItems,
            totalItems: guestCart.cart.totalQuantity,
            total: parseFloat(guestCart.strSubTotal),
            isEmpty: transformedItems.length === 0
          }
        });
      } else {
        const authData = cartData as any;
        dispatch({
          type: "SET_CART",
          payload: {
            items: authData.items,
            totalItems: authData.totalQuantity,
            total: parseFloat(authData.subTotal),
            isEmpty: authData.items.length === 0
          }
        });
      }
    }
  }, [cartData, isLoading]);
  useEffect(() => {
    if (isAuthorized) {
      localStorage.removeItem('guestCartId');
    } else if (cartData && 'id' in cartData) {
      const {
        location
      } = getCurrentLocationAndLanguage();
      if (location === 'kg') {
        localStorage.setItem('guestCartId', String(cartData.id));
      }
    }
  }, [isAuthorized, cartData]);
  const resetCart = () => {
    dispatch({
      type: "RESET_CART"
    });
  };
  const addItemToCart = async (item: CartItem, quantity: number) => {
    if (consolidated && subCart?.id) {
      await addToCartMutation.mutateAsync({
        productCode: item.productCode,
        quantity,
        cartId: subCart.id,
        isAuthorized: true
      });
      dispatch({
        type: "ADD_ITEM_WITH_QUANTITY",
        item,
        quantity
      });
      return;
    }
    if (isAuthorized || location !== 'kg') {
      await addToCartMutation.mutateAsync({
        productCode: item.productCode,
        quantity,
        isAuthorized
      });
    } else {
      try {
        const cartId = localStorage.getItem('guestCartId') ? Number(localStorage.getItem('guestCartId')) : null;
        const result = await addToCartMutation.mutateAsync({
          productCode: item.productCode,
          quantity,
          isAuthorized,
          cartId
        });
        if (result && result.id) {
          localStorage.setItem('guestCartId', String(result.id));
        }
        const existingLocalCart: CartItem[] = JSON.parse(localStorage.getItem('localGuestCart') || '[]');
        const index = existingLocalCart.findIndex((cartItem: CartItem) => cartItem.productCode === item.productCode);
        if (index !== -1) {
          existingLocalCart[index].quantity += quantity;
        } else {
          existingLocalCart.push({
            ...item,
            quantity
          });
        }
        localStorage.setItem('localGuestCart', JSON.stringify(existingLocalCart));
      } catch (error) {
        // Handle error if needed
      }
    }
    dispatch({
      type: "ADD_ITEM_WITH_QUANTITY",
      item,
      quantity
    });
  };
  const removeItemFromCart = async (productCode: string) => {
    try {
      let cartId: number | null = null;
      if (consolidated && subCart?.id) {
        cartId = subCart.id;
      } else if (!isAuthorized && location === 'kg') {
        cartId = localStorage.getItem('guestCartId') ? Number(localStorage.getItem('guestCartId')) : null;
      }
      await removeFromCartMutation.mutateAsync({
        productCode,
        cartId
      });
      dispatch({
        type: "REMOVE_ITEM",
        productCode
      });
    } catch (error) {
      // Handle error if needed
    }
  };
  const updateItemQuantity = async (productCode: string, quantity: number) => {
    try {
      let cartId: number | null = null;
      if (consolidated && subCart?.id) {
        cartId = subCart.id;
      } else if (!isAuthorized && location === 'kg') {
        cartId = localStorage.getItem('guestCartId') ? Number(localStorage.getItem('guestCartId')) : null;
      }
      await updateCartQuantityMutation.mutateAsync({
        productCode,
        quantity,
        cartId
      });
      dispatch({
        type: "UPDATE_ITEM_QUANTITY",
        productCode,
        quantity
      });
    } catch (error) {
      // Handle error if needed
    }
  };
  const isInCart = (productCode: string) => !!state.items.find(item => item.productCode === productCode);
  const getItemFromCart = (productCode: string) => state.items.find(item => item.productCode === productCode);
  useEffect(() => {
    if (!isAuthorized && location === 'kg') {
      localStorage.setItem('localGuestCart', JSON.stringify(state.items));
    }
  }, [state.items, isAuthorized, location]);
  return <CartContext.Provider value={{
    ...state,
    addItemToCart,
    removeItemFromCart,
    updateItemQuantity,
    isInCart,
    getItemFromCart,
    resetCart,
    refetch
  }} data-sentry-element="unknown" data-sentry-component="CartProvider" data-sentry-source-file="cart.context.tsx">
      {children}
    </CartContext.Provider>;
};